import { callsAPI } from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  Container,
  Table,
  Badge,
  Card,
  Row,
  Col,
  Button,
  Form,
  InputGroup,
  Collapse,
  Modal,
  Tabs,
  Tab,
} from "react-bootstrap";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ScheduledCallsTab from "./ScheduledCallsTab";

// Sources that count toward the "Recent Calls" bucket. Must match the
// backend's RECENT_CALL_SOURCES exactly.
const RECENT_CALL_SOURCES = ["inbound_calls", "calls"];
const DASHBOARD_PAGE_SIZE = 100;

const SOURCE_OPTIONS = [
  { value: "all", label: "All sources" },
  { value: "inbound_sms", label: "SMS" },
  { value: "recent_calls", label: "Recent Calls" },
  { value: "sales_leads", label: "Sales Leads" },
  { value: "seasonal_leads", label: "Seasonal Leads" },
  { value: "website_chatbot", label: "Website Chatbot" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "success", label: "Success" },
  { value: "completed", label: "Completed" },
  { value: "qualified", label: "Qualified" },
  { value: "not_qualified", label: "Not Qualified" },
  { value: "failure", label: "Failure" },
  { value: "no_answer", label: "No Answer" },
  { value: "invalid_number", label: "Invalid Number" },
];

const DATE_FILTER_OPTIONS = [
  { value: "all", label: "All dates" },
  { value: "last_week", label: "Last week" },
  { value: "last_two_weeks", label: "Last two weeks" },
  { value: "last_month", label: "Last month" },
];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "name", label: "Name (A-Z)" },
];

const OUTCOME_KEYS_TO_SKIP = ["outcome", "body"];

const useDebouncedValue = (value, delayMs) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
};

const formatKeyLabel = (key) =>
  key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const isTruthyFlag = (value) => {
  if (value === true) return true;
  if (typeof value === "string") return value.trim().toLowerCase() === "yes";
  return false;
};

const wentToVoicemail = (call) =>
  typeof call?.outcome === "object" && call.outcome !== null
    ? isTruthyFlag(call.outcome.voicemail)
    : false;

const formatOutcomeValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  const text = String(value);
  if (text.toLowerCase() === "yes" || text.toLowerCase() === "no") {
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }
  return text;
};

const formatOutcomeEntryValue = (key, value) =>
  key === "voicemail"
    ? isTruthyFlag(value)
      ? "Voicemail"
      : "No Voicemail"
    : formatOutcomeValue(value);

const parseTranscript = (transcript) => {
  if (!transcript) return [];
  return transcript
    .split("\n")
    .filter((line) => line.trim().length > 0)
    .map((line) => {
      const match = line.match(/^(User|Agent|Assistant|Caller)\s*:\s*(.*)$/i);
      if (match) {
        return { speaker: match[1], text: match[2] };
      }
      return { speaker: null, text: line };
    });
};

const isRecentCall = (source) => RECENT_CALL_SOURCES.includes(source);

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString() : "N/A";

const getOutcomeText = (call) => {
  if (!call) return "N/A";

  if (typeof call.outcome === "object" && call.outcome !== null) {
    return (
      call.outcome.outcome ||
      call.outcome.body ||
      (wentToVoicemail(call) ? "Voicemail" : null) ||
      call.qualification ||
      "N/A"
    );
  }

  return call.outcome || call.qualification || "N/A";
};

// Mirrors the backend's _get_qualification_label() (dashboard/sync.py) —
// kept in sync deliberately, this copy only drives display.
const getQualificationText = (call) => {
  const qualification = String(call.qualification || "").trim().toLowerCase();
  const outcome = String(getOutcomeText(call) || "").trim().toLowerCase();

  if (
    qualification === "yes" ||
    qualification === "qualified" ||
    qualification === "ready to start" ||
    qualification === "immediate follow-up" ||
    outcome === "qualified" ||
    outcome === "ready to start"
  ) {
    return "Qualified";
  }

  if (
    qualification === "no" ||
    qualification === "not_qualified" ||
    qualification === "not qualified" ||
    outcome.includes("unqualified")
  ) {
    return "Not Qualified";
  }

  if (!call.qualification && wentToVoicemail(call)) {
    return "Voicemail";
  }

  return call.qualification || getOutcomeText(call) || "N/A";
};

const getBadgeVariant = (value) => {
  const text = String(value || "").toLowerCase();

  if (text === "qualified") return "success";
  if (text === "not qualified") return "danger";
  if (text === "voicemail") return "secondary";
  if (text.includes("success") || text.includes("completed")) return "primary";
  if (text.includes("failure") || text.includes("invalid")) return "danger";
  if (text.includes("no_answer")) return "warning";

  return "secondary";
};

const getSourceLabel = (source) => {
  if (source === "inbound_sms") return "SMS";
  if (source === "sales_leads" || source === "sales_calls") return "Sales";
  if (source === "seasonal_leads" || source === "seasonal_calls") return "Seasonal";
  if (source === "website_chatbot") return "Website";
  if (isRecentCall(source)) return "Recent Calls";
  return source || "N/A";
};

const RecentActivityTab = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [search, setSearch] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);
  const [isChangingScope, setIsChangingScope] = useState(false);

  const quickLinks = [
    {
      label: "Take to Proposal Call History",
      description: "View proposal-related call history",
      path: "/history",
      variant: "primary",
    },
    {
      label: "Sales Calls History",
      description: "Open the sales calls overview",
      path: "/sales-calls",
      variant: "success",
    },
    {
      label: "Inbound Calls History",
      description: "Review inbound call activity",
      path: "/inbound-calls",
      variant: "info",
    },
    {
      label: "SMS History",
      description: "See inbound SMS conversations",
      path: "/inbound-sms",
      variant: "warning",
    },
  ];

  const debouncedSearch = useDebouncedValue(search, 350);

  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, sourceFilter, statusFilter, dateFilter, sortBy]);

  const openSmsDetails = (phone) => {
    if (!phone) return;
    navigate(`/sms-details?phone=${encodeURIComponent(phone)}`);
  };

  const handleRecordActivate = (call) => {
    if (call.source === "inbound_sms") {
      openSmsDetails(call.phone);
    } else {
      setSelectedCall(call);
    }
  };

  const openQuickLink = (path) => {
    navigate(path);
  };

  const handleRecordKeyDown = (event, call) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleRecordActivate(call);
    }
  };

  const { data, isLoading, isError, isFetching, isPlaceholderData } = useQuery({
    queryKey: ["dashboardData", page, debouncedSearch, sourceFilter, statusFilter, dateFilter, sortBy],
    queryFn: async () => {
      const res = await callsAPI.getRecentCalls({
        page,
        page_size: DASHBOARD_PAGE_SIZE,
        search: debouncedSearch.trim() || undefined,
        source: sourceFilter !== "all" ? sourceFilter : undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        date_filter: dateFilter !== "all" ? dateFilter : undefined,
        sort: sortBy,
      });
      return res.data;
    },
    staleTime: 30000,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (isFetching) {
      setIsChangingScope(true);
      return;
    }

    const timer = window.setTimeout(() => setIsChangingScope(false), 180);
    return () => window.clearTimeout(timer);
  }, [isFetching]);

  const records = data?.results || [];
  const qualifiedTotal = data?.qualified_total || 0;
  const qualifiedBreakdown = data?.qualified_breakdown || {};
  const totalPages = data?.total_pages || 1;
  const totalMatching = data?.total_matching_records ?? records.length;
  const totalInFeed = data?.total_records_in_feed ?? totalMatching;
  const lastRefreshedAt = data?.last_refreshed_at;

  const activityStats = data?.activity_stats || {
    sms: 0,
    recent_calls: 0,
    completed: 0,
    failed: 0,
    with_transcript: 0,
  };

  const activeFilterCount = [
    sourceFilter !== "all",
    statusFilter !== "all",
    dateFilter !== "all",
    search.trim().length > 0,
  ].filter(Boolean).length;

  const clearFilters = () => {
    setSearch("");
    setSourceFilter("all");
    setStatusFilter("all");
    setDateFilter("all");
    setSortBy("newest");
  };

  // The dashboard feed is now a stored table (DashboardRecord), not
  // recomputed live on every read. "Refresh" explicitly triggers the
  // backend to re-pull all source tables and rebuild that table, then
  // refetches this query. Plain refetch()/window focus revalidation would
  // just re-read the same stored data — this is the actual resync.
  const handleRefresh = async () => {
    setIsRefreshing(true);
    setRefreshError(null);
    try {
      await callsAPI.refreshDashboardFeed();
      await queryClient.invalidateQueries({ queryKey: ["dashboardData"] });
      await queryClient.refetchQueries({ queryKey: ["dashboardData"], type: "active" });
    } catch (err) {
      setRefreshError("Refresh failed — check the backend logs.");
    } finally {
      setIsRefreshing(false);
    }
  };

  if (isLoading) return <LoadingSpinner text="Loading recent activity..." />;

  if (isError) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center px-3 text-center"
        style={{ minHeight: "60vh" }}
      >
        <p className="text-danger fw-bold mb-3">Failed to load recent activity</p>
        <Button onClick={handleRefresh}>Retry</Button>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex flex-column align-items-start gap-2 mb-3">
        <p className="text-muted small mb-0">
          {lastRefreshedAt
            ? `Last refreshed ${formatDateTime(lastRefreshedAt)}`
            : "Not yet refreshed"}
        </p>

        <div className="d-flex flex-wrap align-items-center gap-2">
          <div className="d-flex flex-column">
            <span className="text-muted" style={{ fontSize: "0.7rem" }}>
              Show records from
            </span>
            <Form.Select
              size="sm"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              aria-label="Filter records by date range"
              className="w-auto"
              style={{ minWidth: 155 }}
            >
              {DATE_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </div>

          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
            aria-label="Refresh dashboard data"
            className="flex-shrink-0"
          >
            <span className="d-none d-sm-inline">
              {isRefreshing ? "Refreshing…" : "Refresh"}
            </span>
            <span className="d-inline d-sm-none" aria-hidden="true">
              {isRefreshing ? "…" : "⟳"}
            </span>
          </Button>
        </div>
      </div>

      {refreshError && <div className="text-danger small mb-3">{refreshError}</div>}

      {isChangingScope && (
        <div className="d-flex align-items-center gap-2 text-muted small mb-3">
          <div className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          <span>Loading {dateFilter === "all" ? "records" : `${DATE_FILTER_OPTIONS.find((option) => option.value === dateFilter)?.label.toLowerCase() || "selected range"} data`}…</span>
        </div>
      )}

      <Row className="g-2 mb-3">
        <Col xs={6} md={true}>
          <StatCard label="Qualified" value={qualifiedTotal} valueClass="text-success" />
        </Col>
        <Col xs={6} md={true}>
          <StatCard label="SMS" value={activityStats.sms} />
        </Col>
        <Col xs={6} md={true}>
          <StatCard label="Recent Calls" value={activityStats.recent_calls} />
        </Col>
        <Col xs={6} md={true}>
          <StatCard label="Failed" value={activityStats.failed} valueClass="text-danger" />
        </Col>
        <Col xs={6} md={true}>
          <StatCard label="Transcripts" value={activityStats.with_transcript} />
        </Col>
      </Row>

      <Card className="border-0 shadow-sm mb-3">
        <Card.Body className="p-2 px-3">
          <div className="d-flex flex-wrap gap-2 small">
            <Badge bg="primary">Sales: {qualifiedBreakdown.sales_leads || 0}</Badge>
            <Badge bg="info">Seasonal: {qualifiedBreakdown.seasonal_leads || 0}</Badge>
            <Badge bg="dark">Website: {qualifiedBreakdown.website_chatbot || 0}</Badge>
            <Badge bg="success">
              Recent Calls:{" "}
              {(qualifiedBreakdown.inbound_calls || 0) +
                (qualifiedBreakdown.calls || 0)}
            </Badge>
            <Badge bg="warning" text="dark">
              SMS: {qualifiedBreakdown.sms_qualified || 0}
            </Badge>
          </div>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm mb-3" style={{ background: "linear-gradient(135deg, #f8fbff 0%, #f5f7fb 100%)" }}>
        <Card.Body className="p-3 p-lg-4">
          <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-2 mb-3">
            <div>
              <div className="fw-semibold text-dark">History shortcuts</div>
              <div className="small text-muted">
                Jump quickly to the main proposal, sales, inbound, and SMS history views.
              </div>
            </div>
          </div>

          <Row className="g-2">
            {quickLinks.map((link) => (
              <Col xs={12} sm={6} lg={3} key={link.label}>
                <Button
                  variant={`outline-${link.variant}`}
                  className="w-100 text-start py-2 px-3 h-100 border-2"
                  onClick={() => openQuickLink(link.path)}
                  style={{ borderRadius: 12 }}
                >
                  <div className="fw-semibold">{link.label}</div>
                  <div className="small text-muted mt-1">{link.description}</div>
                </Button>
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm mb-3">
        <Card.Body className="p-2 px-3">
          <div className="d-flex justify-content-between align-items-center d-md-none">
            <Button
              variant="light"
              size="sm"
              onClick={() => setFiltersOpen((open) => !open)}
              aria-expanded={filtersOpen}
              className="d-flex align-items-center gap-2"
            >
              Filters
              {activeFilterCount > 0 && (
                <Badge bg="primary" pill>
                  {activeFilterCount}
                </Badge>
              )}
              <span aria-hidden="true">{filtersOpen ? "▲" : "▼"}</span>
            </Button>

            {activeFilterCount > 0 && (
              <Button variant="link" size="sm" className="text-decoration-none" onClick={clearFilters}>
                Clear
              </Button>
            )}
          </div>

          <Collapse in={filtersOpen} className="d-md-none">
            <div className="pt-2">
              <FilterControls
                search={search}
                setSearch={setSearch}
                sourceFilter={sourceFilter}
                setSourceFilter={setSourceFilter}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                sortBy={sortBy}
                setSortBy={setSortBy}
                isSearching={isFetching && debouncedSearch !== search}
              />
            </div>
          </Collapse>

          <div className="d-none d-md-flex justify-content-between align-items-start gap-3">
            <div className="flex-grow-1">
              <FilterControls
                search={search}
                setSearch={setSearch}
                sourceFilter={sourceFilter}
                setSourceFilter={setSourceFilter}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                dateFilter={dateFilter}
                setDateFilter={setDateFilter}
                sortBy={sortBy}
                setSortBy={setSortBy}
                isSearching={isFetching && debouncedSearch !== search}
              />
            </div>

            {activeFilterCount > 0 && (
              <Button variant="link" size="sm" className="text-decoration-none flex-shrink-0" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-2 p-sm-3">
          <div className="d-flex flex-column flex-sm-row justify-content-between gap-1 mb-2 px-1">
            <div>
              <div className="fw-semibold small d-flex align-items-center gap-2">
                Latest Records
                {isFetching && isPlaceholderData && (
                  <span className="text-muted fw-normal" style={{ fontSize: "0.75rem" }}>
                    Updating…
                  </span>
                )}
              </div>
              <div className="small text-muted">
                {activeFilterCount > 0
                  ? `${totalMatching} matching filters (of ${totalInFeed} total)`
                  : `${totalInFeed} total records`}
                <span className="ms-2">• Showing {DASHBOARD_PAGE_SIZE} per page</span>
              </div>
            </div>
            <div className="small text-muted">Page {page} of {totalPages}</div>
          </div>

          <div className="d-md-none">
            {records.length === 0 ? (
              <EmptyState onClear={activeFilterCount > 0 ? clearFilters : undefined} />
            ) : (
              records.map((call) => (
                <RecordCard
                  key={`${call.source}-${call.record_id}`}
                  call={call}
                  onActivate={handleRecordActivate}
                  onKeyDownActivate={handleRecordKeyDown}
                />
              ))
            )}
          </div>

          <div className="d-none d-md-block">
            {records.length === 0 ? (
              <EmptyState onClear={activeFilterCount > 0 ? clearFilters : undefined} />
            ) : (
              <div className="table-responsive">
                <Table hover size="sm" className="mb-0 align-middle small">
                  <thead className="table-light">
                    <tr>
                      <th>Time</th>
                      <th>Contact</th>
                      <th>Address</th>
                      <th>Source</th>
                      <th>Status</th>
                      <th>Qualification</th>
                      <th></th>
                    </tr>
                  </thead>

                  <tbody>
                    {records.map((call) => {
                      const isSms = call.source === "inbound_sms";
                      const qualificationText = getQualificationText(call);

                      return (
                        <tr
                          key={`${call.source}-${call.record_id}`}
                          role="button"
                          tabIndex={0}
                          aria-label={`Open details for ${call.name || call.phone || "record"}`}
                          onClick={() => handleRecordActivate(call)}
                          onKeyDown={(e) => handleRecordKeyDown(e, call)}
                          style={{ cursor: "pointer" }}
                        >
                          <td className="text-nowrap" style={{ minWidth: 150 }}>
                            {formatDateTime(call.timestamp)}
                          </td>

                          <td style={{ minWidth: 180 }}>
                            <div className="fw-semibold">{call.name || "N/A"}</div>
                            <div className="text-muted">{call.phone || "N/A"}</div>
                          </td>

                          <td style={{ minWidth: 200, maxWidth: 260 }} className="text-truncate">
                            {call.address || "N/A"}
                          </td>

                          <td>
                            <Badge
                              bg={isSms ? "warning" : "dark"}
                              text={isSms ? "dark" : undefined}
                            >
                              {getSourceLabel(call.source)}
                            </Badge>
                          </td>

                          <td>
                            <Badge bg={getBadgeVariant(call.status)}>
                              {call.status || "N/A"}
                            </Badge>
                          </td>

                          <td>
                            <Badge bg={getBadgeVariant(qualificationText)}>
                              {qualificationText}
                            </Badge>
                          </td>

                          <td className="text-primary text-nowrap">
                            {call.transcript && <span title="Has transcript">📄 </span>}
                            {wentToVoicemail(call) && <span title="Went to voicemail">📞 </span>}
                            {isSms ? "Open SMS" : "Details"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </div>

          <div className="d-flex flex-row justify-content-between align-items-center gap-2 mt-3">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={page <= 1 || (isFetching && isPlaceholderData)}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              ← Previous {DASHBOARD_PAGE_SIZE}
            </Button>

            <span className="small text-muted text-center">
              Page {page} of {totalPages}
              <span className="d-none d-sm-inline"> • {DASHBOARD_PAGE_SIZE} records/page</span>
            </span>

            <Button
              variant="outline-secondary"
              size="sm"
              disabled={page >= totalPages || (isFetching && isPlaceholderData)}
              onClick={() => setPage((current) => current + 1)}
            >
              Next {DASHBOARD_PAGE_SIZE} →
            </Button>
          </div>
        </Card.Body>
      </Card>

      <RecordDetailModal call={selectedCall} onClose={() => setSelectedCall(null)} />
    </>
  );
};

const StatCard = ({ label, value, valueClass = "" }) => (
  <Card className="border-0 shadow-sm h-100">
    <Card.Body className="p-2 px-3">
      <div className="small text-muted text-truncate">{label}</div>
      <div className={`h5 mb-0 ${valueClass}`}>{value}</div>
    </Card.Body>
  </Card>
);

const FilterControls = ({
  search,
  setSearch,
  sourceFilter,
  setSourceFilter,
  statusFilter,
  setStatusFilter,
  dateFilter,
  setDateFilter,
  sortBy,
  setSortBy,
  isSearching,
}) => (
  <Row className="g-2">
    <Col xs={12} lg={4}>
      <InputGroup size="sm">
        <InputGroup.Text>{isSearching ? "…" : "🔍"}</InputGroup.Text>
        <Form.Control
          placeholder="Name, phone, address, transcript..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search records"
        />
      </InputGroup>
    </Col>

    <Col xs={6} lg={3}>
      <Form.Select
        size="sm"
        value={sourceFilter}
        onChange={(e) => setSourceFilter(e.target.value)}
        aria-label="Filter by source"
      >
        {SOURCE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Form.Select>
    </Col>

    <Col xs={6} lg={3}>
      <Form.Select
        size="sm"
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        aria-label="Filter by status"
      >
        {STATUS_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Form.Select>
    </Col>

    <Col xs={12} lg={2}>
      <Form.Select
        size="sm"
        value={sortBy}
        onChange={(e) => setSortBy(e.target.value)}
        aria-label="Sort records"
      >
        {SORT_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Form.Select>
    </Col>
  </Row>
);

const RecordCard = ({ call, onActivate, onKeyDownActivate }) => {
  const isSms = call.source === "inbound_sms";
  const qualificationText = getQualificationText(call);

  return (
    <Card
      className="border-0 shadow-sm mb-2"
      role="button"
      tabIndex={0}
      aria-label={`Open details for ${call.name || call.phone || "record"}`}
      onClick={() => onActivate(call)}
      onKeyDown={(e) => onKeyDownActivate(e, call)}
      style={{ cursor: "pointer" }}
    >
      <Card.Body className="p-2 px-3">
        <div className="d-flex justify-content-between align-items-start gap-2">
          <div className="flex-grow-1 min-width-0">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="fw-semibold">{call.name || "N/A"}</span>
              {call.transcript && <span title="Has transcript">📄</span>}
              {wentToVoicemail(call) && <span title="Went to voicemail">📞</span>}
            </div>
            <div className="small text-muted">{call.phone || "N/A"}</div>
          </div>
          <div className="text-end flex-shrink-0">
            <Badge bg={isSms ? "warning" : "dark"} text={isSms ? "dark" : undefined} className="mb-1">
              {getSourceLabel(call.source)}
            </Badge>
            <div>
              <Badge bg={getBadgeVariant(call.status)}>{call.status || "N/A"}</Badge>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center small text-muted mt-1">
          <span className="text-truncate">{call.address || "No address"}</span>
          <span className="text-nowrap ms-2">{formatDateTime(call.timestamp)}</span>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-2">
          <Badge bg={getBadgeVariant(qualificationText)}>{qualificationText}</Badge>
          <span className="small text-primary fw-semibold">
            {isSms ? "Open SMS →" : "View details →"}
          </span>
        </div>
      </Card.Body>
    </Card>
  );
};

const RecordDetailModal = ({ call, onClose }) => {
  if (!call) return null;

  const qualificationText = getQualificationText(call);
  const transcriptLines = parseTranscript(call.transcript);

  const outcomeEntries =
    typeof call.outcome === "object" && call.outcome !== null
      ? Object.entries(call.outcome).filter(
          ([key, value]) =>
            !OUTCOME_KEYS_TO_SKIP.includes(key) &&
            value !== null &&
            value !== undefined &&
            value !== ""
        )
      : [];

  return (
    <Modal show onHide={onClose} size="lg" scrollable centered>
      <Modal.Header closeButton>
        <div>
          <Modal.Title className="h6 mb-1">{call.name || "N/A"}</Modal.Title>
          <div className="d-flex flex-wrap gap-2">
            <Badge bg="dark">{getSourceLabel(call.source)}</Badge>
            <Badge bg={getBadgeVariant(call.status)}>{call.status || "N/A"}</Badge>
            <Badge bg={getBadgeVariant(qualificationText)}>{qualificationText}</Badge>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="small">
        <Row className="g-2 mb-3">
          <Col xs={6}>
            <div className="text-muted">Phone</div>
            <div className="fw-semibold">{call.phone || "N/A"}</div>
          </Col>
          <Col xs={6}>
            <div className="text-muted">Time</div>
            <div className="fw-semibold">{formatDateTime(call.timestamp)}</div>
          </Col>
          <Col xs={12}>
            <div className="text-muted">Address</div>
            <div className="fw-semibold">{call.address || "N/A"}</div>
          </Col>
          {call.call_type && (
            <Col xs={6}>
              <div className="text-muted">Call Type</div>
              <div className="fw-semibold text-capitalize">{call.call_type}</div>
            </Col>
          )}
          {call.raw_qualification && (
            <Col xs={6}>
              <div className="text-muted">Raw Qualification</div>
              <div className="fw-semibold">{call.raw_qualification}</div>
            </Col>
          )}
        </Row>

        {outcomeEntries.length > 0 && (
          <div className="mb-3">
            <div className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>
              Outcome Details
            </div>
            <Row className="g-2">
              {outcomeEntries.map(([key, value]) => (
                <Col xs={6} key={key}>
                  <div className="text-muted">{formatKeyLabel(key)}</div>
                  <div className="fw-semibold">{formatOutcomeEntryValue(key, value)}</div>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {call.transcript ? (
          <div>
            <div className="text-muted text-uppercase mb-2" style={{ fontSize: "0.75rem" }}>
              Transcript
            </div>
            <div
              className="border rounded p-2 bg-light"
              style={{ maxHeight: 320, overflowY: "auto" }}
            >
              {transcriptLines.map((line, idx) => (
                <div key={idx} className="mb-2">
                  {line.speaker && (
                    <span className="fw-semibold me-1">{line.speaker}:</span>
                  )}
                  <span>{line.text}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-muted fst-italic">
            {wentToVoicemail(call)
              ? "No transcript — the call went to voicemail."
              : "No transcript available for this record."}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

const EmptyState = ({ onClear }) => (
  <div className="text-center py-5">
    <p className="text-muted mb-2">No records match your filters.</p>
    {onClear && (
      <Button variant="outline-secondary" size="sm" onClick={onClear}>
        Clear filters
      </Button>
    )}
  </div>
);

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState("recent");

  return (
    <Container fluid className="dashboard-page py-3 px-2 px-sm-3 px-xl-4">
      <div className="mb-3">
        <h1 className="h5 mb-1">Dashboard</h1>
        <p className="text-muted small mb-0 d-none d-sm-block">
          Recent activity across all sources, plus upcoming scheduled calls.
        </p>
      </div>

      <Tabs
        activeKey={activeTab}
        onSelect={(key) => setActiveTab(key || "recent")}
        className="mb-3"
      >
        <Tab eventKey="recent" title="Recent Activity">
          <div className="pt-3">
            <RecentActivityTab />
          </div>
        </Tab>
        <Tab eventKey="scheduled" title="Scheduled Calls">
          <div className="pt-3">
            <ScheduledCallsTab />
          </div>
        </Tab>
      </Tabs>
    </Container>
  );
};

export default Dashboard;